/**
 * [Phase 2c.6-A.1.10] Client-side snake-draft Adam-voice template synthesis.
 *
 * Mirrors backend/engines/snake_templates.py + backend/engines/nfl_teams.py
 * byte-for-byte via the shared JSON pool at /app/shared/voice_pools.json.
 * Divergence is mechanically impossible: both languages import the same file.
 *
 * Why client-side: the auction live feed works perfectly because it
 * synthesizes lines from WS events with zero DB roundtrip (DraftContext's
 * `sayAdam` pattern). Snake's prior backend-write → WS-broadcast → REST-hydrate
 * pipeline failed 5 consecutive sub-checkpoints in the wild - the "iter
 * green, founder broken" pattern logged in PRD.md / TD-44. A.1.10 ports
 * exactly auction's discipline to snake.
 *
 * Public API (parallel to snake_templates.py):
 *   pickMadeTemplate(teamName, playerName, nflTeamAbbr?, rng?) → string
 *   roundStartTemplate(roundN, rng?) → string
 *   nflTeamPhrase(abbr, rng?) → string
 *
 * `rng` is an optional `() => number` (in [0, 1)) for deterministic tests.
 * Production callers omit it; the default uses Math.random.
 */
import POOLS from "../shared/voice_pools.json";

const _PICK_VERBS = POOLS.pick_verbs;
const _ROUND_START_PHRASES = POOLS.round_start_phrases;
const _NFL_TEAM_VARIANTS = POOLS.nfl_team_variants;

// Exported for the parity test + variety unit tests.
export const PICK_VERBS = _PICK_VERBS;
export const ROUND_START_PHRASES = _ROUND_START_PHRASES;
export const NFL_TEAM_VARIANTS = _NFL_TEAM_VARIANTS;

function _pick(arr, rng) {
  const r = typeof rng === "function" ? rng : Math.random;
  return arr[Math.floor(r() * arr.length)];
}

/**
 * Return a randomly-selected variant of the team's name suitable for TTS.
 *
 * Unambiguous-city teams pick uniformly from {city, nickname, full}.
 * Ambiguous-city teams (LAC, LAR, NYG, NYJ) pick from {nickname, full} - the
 * `city` slot is null in the shared JSON so the filter naturally skips it.
 * Unknown abbreviations fall back to the abbreviation unchanged (safe - at
 * worst Adam reads "S F", which is the pre-HF-2 status quo).
 *
 * [HF3 P1-3] `withArticle=true` returns a prefix-ready phrase that drops
 * cleanly into "of ${phrase}." sentences with correct grammar:
 *   - city variant ("Baltimore")        → "Baltimore"            (no article)
 *   - nickname variant ("Ravens")       → "the Ravens"
 *   - full variant ("Baltimore Ravens") → "the Baltimore Ravens"
 */
export function nflTeamPhrase(abbr, rng, options) {
  if (!abbr) return abbr || "";
  const variants = _NFL_TEAM_VARIANTS[abbr];
  if (!variants) return abbr;
  const poolWithKind = [
    ["city", variants.city],
    ["nickname", variants.nickname],
    ["full", variants.full],
  ].filter(([, v]) => Boolean(v));
  if (poolWithKind.length === 0) return abbr;
  const [kind, value] = _pick(poolWithKind, rng);
  const withArticle = options && options.withArticle === true;
  if (withArticle && (kind === "nickname" || kind === "full")) {
    return `the ${value}`;
  }
  return value;
}

/**
 * One short Adam line announcing a pick. Includes team phrase for TTS
 * clarity (Q2=a from the v1.1 spec). Verb chosen randomly from PICK_VERBS.
 *
 * [HF3 P1-3] Grammar fix: requests `withArticle: true` so the helper returns
 * "Baltimore" / "the Ravens" / "the Baltimore Ravens" and the template
 * writes "of ${phrase}." - produces "of Baltimore", "of the Ravens",
 * "of the Kansas City Chiefs". All grammatically correct.
 *
 * Example: "Stat Stalkers picks Patrick Mahomes of the Chiefs."
 */
export function pickMadeTemplate(teamName, playerName, nflTeamAbbr, rng) {
  const verb = _pick(_PICK_VERBS, rng);
  const safeTeam = (teamName || "Team").trim() || "Team";
  const safePlayer = (playerName || "the pick").trim() || "the pick";
  const abbr = (nflTeamAbbr || "").trim();
  if (abbr) {
    const teamPhrase = nflTeamPhrase(abbr, rng, { withArticle: true });
    return `${safeTeam} ${verb} ${safePlayer} of ${teamPhrase}.`;
  }
  return `${safeTeam} ${verb} ${safePlayer}.`;
}

/** Round-boundary marker. Phrase chosen randomly for variety. */
export function roundStartTemplate(roundN, rng) {
  const phrase = _pick(_ROUND_START_PHRASES, rng);
  return phrase.replace("{n}", String(roundN));
}

/**
 * Round-boundary detector. Mirrors the backend logic in
 * engines/snake_draft.py:_dispatch_pick_voice (pre-A.1.10) verbatim:
 *   • fires when the just-completed pick was the last of a round
 *   • does NOT fire after the final pick of the draft (no next round)
 *
 * Returns the round number that is STARTING (i.e., finishedRound + 1), or
 * `null` if no boundary was crossed.
 *
 * Inputs come from the PICK_MADE WS frame + draftState:
 *   pick.round, pick.pick_in_round, pick.overall_pick
 *   teamCount = draftState.team_count
 *   rosterSize = draftState.roster_size
 */
export function detectRoundBoundary(pick, teamCount, rosterSize) {
  if (!pick) return null;
  const finishedRound = Number(pick.round) || 0;
  const finishedPickInRound = Number(pick.pick_in_round) || 0;
  const overallPick = Number(pick.overall_pick) || 0;
  const totalPicks = (Number(teamCount) || 0) * (Number(rosterSize) || 0);
  if (finishedRound <= 0) return null;
  if (finishedPickInRound !== Number(teamCount)) return null;
  if (overallPick + 1 > totalPicks) return null;
  return finishedRound + 1;
}
